import styled, { css, keyframes } from 'styled-components';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(108, 92, 231, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(108, 92, 231, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(108, 92, 231, 0);
  }
`;

const shine = keyframes`
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.small ? '0.6rem 1.2rem' : '0.875rem 1.75rem'};
  border-radius: 12px;
  font-weight: 600;
  font-size: ${props => props.small ? '0.875rem' : '1rem'};
  cursor: pointer;
  transition: ${props => props.theme.transition.default};
  position: relative;
  overflow: hidden;
  letter-spacing: 0.02em;
  font-family: ${props => props.theme.fonts.main};
  
  background: ${props => {
    if (props.variant === 'outline') return 'transparent';
    if (props.variant === 'secondary') return props.theme.colors.gradient2;
    if (props.variant === 'accent') return props.theme.colors.gradient3;
    if (props.variant === 'danger') return 'linear-gradient(to right, #d63031, #e84393)';
    if (props.variant === 'success') return 'linear-gradient(to right, #00b894, #00cec9)';
    return props.theme.colors.gradient1;
  }};
  
  color: ${props => {
    if (props.variant === 'outline') return props.theme.colors.primary;
    return props.theme.colors.white;
  }};
  
  border: ${props => {
    if (props.variant === 'outline') return `2px solid ${props.theme.colors.primary}`;
    return 'none';
  }};
  
  box-shadow: ${props => {
    if (props.variant === 'outline') return 'none';
    return props.theme.boxShadow.button;
  }};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: ${props => props.theme.transition.default};
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.boxShadow.hover};
    
    &::before {
      animation: ${shine} 1s ease-in-out;
    }
    
    background: ${props => {
      if (props.variant === 'outline') return 'rgba(108, 92, 231, 0.1)';
      if (props.variant === 'secondary') return 'linear-gradient(to right, #00a884, #55efc4)';
      if (props.variant === 'accent') return 'linear-gradient(to right, #e84393, #fd79a8)';
      if (props.variant === 'danger') return 'linear-gradient(to right, #c0392b, #d63031)';
      if (props.variant === 'success') return 'linear-gradient(to right, #00a884, #00b894)';
      return 'linear-gradient(to right, #5849c2, #6c5ce7)';
    }};
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.boxShadow.small};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    
    &::before {
      display: none;
    }
  }
  
  ${props => props.pulse && css`
    animation: ${pulse} 2s infinite;
  `}
  
  ${props => props.glass && css`
    background: rgba(108, 92, 231, 0.1);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(108, 92, 231, 0.2);
    color: ${props => props.theme.colors.text};
    
    &:hover {
      background: rgba(108, 92, 231, 0.2);
      border-color: rgba(108, 92, 231, 0.3);
    }
  `}
  
  svg {
    margin-right: ${props => props.iconOnly ? '0' : '0.5rem'};
    font-size: ${props => props.small ? '0.9rem' : '1.1rem'};
  }
`;

export default Button; 